import { useSnackbar } from 'notistack';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { VacationBalances } from '~/data/absences';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { putEmpVacBalanceData } from '~/scenes/PeopleModule/components/Tabs/AbsencesTab/components/VacationBalanceData/data';

export type IFormInput = {
  fVacationBalance: number;
  fVacationBalanceBank1: number;
  fVacationBalanceBank2: number;
  fVacationBalanceBank3: number;
  fVacationBalanceEmpId: number;
  fVacationBalanceExtra: number;
  fVacationBalanceSuppl1: number;
  fVacationBalanceSuppl1Spent: number;
  fVacationBalanceSuppl2: number;
  fVacationBalanceSuppl2Spent: number;
};

type PropsType = {
  defaultData?: VacationBalances;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  selectedEmployeeId: number;
  onUpdate?: () => void;
};

export const useVacationBalanceDataForm = (props: PropsType) => {
  const { defaultData, setEditMode, selectedEmployeeId, onUpdate } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Form validation
  const validationSchema = useMemo(() => {
    return yup.object().shape<IFormInput>({
      fVacationBalance: yup.number().required(t('laFillAllFields')),
      fVacationBalanceBank1: yup.number(),
      fVacationBalanceBank2: yup.number(),
      fVacationBalanceBank3: yup.number(),
      fVacationBalanceEmpId: yup.number(),
      fVacationBalanceExtra: yup.number(),
      fVacationBalanceSuppl1: yup.number(),
      fVacationBalanceSuppl1Spent: yup.number(),
      fVacationBalanceSuppl2: yup.number(),
      fVacationBalanceSuppl2Spent: yup.number(),
    });
  }, [t]);

  const resolver = useYupValidationResolver(validationSchema);

  // Setup form
  const initialFieldValues: IFormInput | {} = useMemo(() => {
    if (!!defaultData) {
      return {
        ...defaultData,
      };
    }
    return {};
  }, [defaultData]);

  const form = useForm<IFormInput>({
    mode: 'all',
    defaultValues: initialFieldValues,
    resolver: resolver as never,
  });

  const { control, reset, handleSubmit } = form;

  useEffect(() => {
    reset(initialFieldValues);
  }, [initialFieldValues, reset]);

  const handleFormReset = () => {
    reset();
    setEditMode((prevValue) => !prevValue);
  };

  const handleFormSubmit = useMemo(() => {
    return handleSubmit(
      (formData) => {
        const postData: IFormInput = {
          fVacationBalance: formData.fVacationBalance,
          fVacationBalanceBank1: formData.fVacationBalanceBank1,
          fVacationBalanceBank2: formData.fVacationBalanceBank2,
          fVacationBalanceBank3: formData.fVacationBalanceBank3,
          fVacationBalanceEmpId: formData.fVacationBalanceEmpId,
          fVacationBalanceExtra: formData.fVacationBalanceExtra,
          fVacationBalanceSuppl1: formData.fVacationBalanceSuppl1,
          fVacationBalanceSuppl1Spent: formData.fVacationBalanceSuppl1Spent,
          fVacationBalanceSuppl2: formData.fVacationBalanceSuppl2,
          fVacationBalanceSuppl2Spent: formData.fVacationBalanceSuppl2Spent,
        };

        (async () => {
          try {
            const response = await putEmpVacBalanceData(
              postData,
              selectedEmployeeId,
            );
            if (response) {
              enqueueSnackbar(t('change_saved'), {
                variant: 'success',
              });
              setEditMode(false);
              if (!!onUpdate) {
                onUpdate();
              }
            }
          } catch (error) {
            enqueueSnackbar(t('errorMsg'), {
              variant: 'error',
            });
          }
        })();
      },
      (errors) => {
        Object.values(errors).forEach((fieldError) => {
          enqueueSnackbar(fieldError.message, {
            variant: 'error',
          });
        });
      },
    );
  }, [
    enqueueSnackbar,
    handleSubmit,
    setEditMode,
    t,
    selectedEmployeeId,
    onUpdate,
  ]);

  return {
    control,
    handleFormReset,
    handleFormSubmit,
  };
};
